import React from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Wallet, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  ShoppingCart, 
  Zap, 
  Car, 
  Pill,
  Building2,
  Check,
  Clock,
  X
} from 'lucide-react';

const TRANSACTION_ICONS = {
  'Top-Up': ArrowDownLeft,
  'Grocery Purchase': ShoppingCart,
  'Food Purchase': ShoppingCart,
  'Utility Top-Up': Zap,
  'Transport': Car,
  'Medication': Pill,
  'Hospital': Building2,
  'Miscellaneous': ArrowUpRight,
};

const WalletPage = () => {
  const { currentUser, getSubscriberTransactions, approveTransaction, role, getDependantById } = useApp();
  const transactions = getSubscriberTransactions(currentUser?.id);

  const pendingTxns = transactions.filter(t => t.status === 'Pending Approval');
  const completedTxns = transactions.filter(t => t.status === 'Completed' || t.status === 'Approved');

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency === 'CAD' ? 'C$' : '€';
    return `${symbol}${amount?.toFixed(2) || '0.00'}`;
  };

  const getWalletColorClass = (balance) => {
    if (balance >= 100) return 'text-green-600';
    if (balance >= 20) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
      case 'Approved':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'Pending Approval':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'Declined':
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
      case 'Approved':
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>;
      case 'Pending Approval':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'Declined':
        return <Badge className="bg-red-100 text-red-700">Declined</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="wallet-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">
              {role === 'care_officer' ? 'Wallet Requests' : 'Wallet'}
            </h1>
            <p className="text-gray-500 mt-1">
              {role === 'care_officer' 
                ? 'Submit and track expense requests' 
                : 'Manage your care fund balance and transactions'}
            </p>
          </div>
          {role === 'subscriber' && (
            <Button className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Top Up Wallet
            </Button>
          )}
        </div>

        {/* Balance Card - Subscriber only */}
        {role === 'subscriber' && (
          <Card className="bg-burgundy-700 text-white overflow-hidden rounded-2xl mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm">Available Balance</p>
                  <p className="text-4xl font-serif font-bold mt-1">
                    {formatCurrency(currentUser?.walletBalance, currentUser?.walletCurrency)}
                  </p>
                  <p className="text-white/70 text-sm mt-2">
                    {currentUser?.walletCurrency} · {currentUser?.plan} Plan
                  </p>
                </div>
                <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center">
                  <Wallet className="w-10 h-10 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Approvals */}
        {pendingTxns.length > 0 && (
          <Card className="bg-white rounded-2xl border border-gray-100 mb-8">
            <CardHeader className="pb-3 px-6 pt-5">
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <Clock className="w-5 h-5 text-yellow-600" />
                Pending Approvals
                <Badge className="bg-yellow-100 text-yellow-700 rounded-full ml-auto">{pendingTxns.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <div className="space-y-4">
                {pendingTxns.map(txn => {
                  const Icon = TRANSACTION_ICONS[txn.type] || ArrowUpRight;
                  const dependant = getDependantById(txn.dependantId);

                  return (
                    <div 
                      key={txn.id}
                      className="p-5 bg-yellow-50 rounded-xl border border-yellow-200"
                      data-testid={`pending-txn-${txn.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-yellow-200 flex items-center justify-center">
                            <Icon className="w-6 h-6 text-yellow-700" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{txn.type}</p>
                            <p className="text-sm text-gray-500">
                              {dependant ? `For ${dependant.firstName} ${dependant.lastName}` : 'General'}
                            </p>
                            {txn.vendor && (
                              <p className="text-xs text-gray-500 mt-1">
                                Vendor: {txn.vendor}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mt-2">{txn.notes}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-serif font-bold text-gray-900">{formatCurrency(txn.amount, txn.currency)}</p>
                          {txn.amountNGN && (
                            <p className="text-xs text-gray-500">≈ ₦{txn.amountNGN.toLocaleString()}</p>
                          )}
                          <div className="flex gap-2 mt-4">
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 rounded-full"
                              onClick={() => approveTransaction(txn.id)}
                            >
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-300 rounded-full">
                              Decline
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        <Card className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <CardHeader className="pb-3 px-6 pt-5">
            <CardTitle className="text-lg font-serif">Transaction History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs defaultValue="all">
              <div className="border-b border-gray-100 px-5">
                <TabsList className="bg-transparent h-14">
                  <TabsTrigger value="all" className="rounded-full">All</TabsTrigger>
                  <TabsTrigger value="completed" className="rounded-full">Completed</TabsTrigger>
                  <TabsTrigger value="topups" className="rounded-full">Top-Ups</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all" className="p-5">
                <div className="space-y-3">
                  {transactions.map(txn => {
                    const Icon = TRANSACTION_ICONS[txn.type] || ArrowUpRight;
                    const dependant = getDependantById(txn.dependantId);
                    const isTopUp = txn.type === 'Top-Up';

                    return (
                      <div 
                        key={txn.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isTopUp ? 'bg-green-100' : 'bg-burgundy-100'
                          }`}>
                            <Icon className={`w-6 h-6 ${isTopUp ? 'text-green-600' : 'text-burgundy-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{txn.type}</p>
                            <p className="text-sm text-gray-500">
                              {dependant 
                                ? `${dependant.firstName} ${dependant.lastName}` 
                                : txn.vendor || 'Wallet'}
                              {' · '}
                              {new Date(txn.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className={`font-semibold ${isTopUp ? 'text-green-600' : 'text-gray-900'}`}>
                              {isTopUp ? '+' : '-'}{formatCurrency(txn.amount, txn.currency)}
                            </p>
                            {txn.amountNGN && !isTopUp && (
                              <p className="text-xs text-gray-500">₦{txn.amountNGN.toLocaleString()}</p>
                            )}
                          </div>
                          {getStatusBadge(txn.status)}
                        </div>
                      </div>
                    );
                  })}

                  {transactions.length === 0 && (
                    <div className="text-center py-12">
                      <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No transactions yet</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="completed" className="p-5">
                <div className="space-y-3">
                  {completedTxns.map(txn => {
                    const Icon = TRANSACTION_ICONS[txn.type] || ArrowUpRight;
                    const isTopUp = txn.type === 'Top-Up';

                    return (
                      <div 
                        key={txn.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isTopUp ? 'bg-green-100' : 'bg-burgundy-100'
                          }`}>
                            <Icon className={`w-6 h-6 ${isTopUp ? 'text-green-600' : 'text-burgundy-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{txn.type}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(txn.completedAt || txn.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <p className={`font-semibold ${isTopUp ? 'text-green-600' : 'text-gray-900'}`}>
                          {isTopUp ? '+' : '-'}{formatCurrency(txn.amount, txn.currency)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="topups" className="p-5">
                <div className="space-y-3">
                  {transactions.filter(t => t.type === 'Top-Up').map(txn => (
                    <div 
                      key={txn.id}
                      className="flex items-center justify-between p-4 bg-green-50 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                          <ArrowDownLeft className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Wallet Top-Up</p>
                          <p className="text-sm text-gray-500">
                            {new Date(txn.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold text-green-600">
                        +{formatCurrency(txn.amount, txn.currency)}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
